/* eslint no-template-curly-in-string: 0 */
import { object, string, bool, setLocale, addMethod, number, mixed, StringSchema, array } from 'yup';
import yup from '@raisin/yup-validations';
import parsePhoneNumber from 'libphonenumber-js';

// Minimum currency value
addMethod(mixed, 'currencyMinValue', function minValue(currencyMinValue) {
  return this.test(
    'currencyMinValue',
    currencyMinValue,
    function testMinValue(value) {
      const { path, createError } = this;

      const internalValue =
        typeof value === 'string' ? Number(value.replace(/\D/g, '')) : value;

      if (!value) {
        return createError({
          path,
          message: '${path} is een verplicht veld',
        });
      }

      if (internalValue >= currencyMinValue) {
        return true;
      }

      return createError({
        path,
        message: `De minimale waarde is ${formatMoney(currencyMinValue)}`,
      });
    },
  );
});

// Maximum currency value
addMethod(mixed, 'currencyMaxValue', function maxValue(currencyMaxValue) {
  return this.test(
    'currencyMaxValue',
    currencyMaxValue,
    function testMaxValue(value) {
      const { path, createError } = this;

      const internalValue =
        typeof value === 'string' ? Number(value.replace(/\D/g, '')) : value;

      if (internalValue <= currencyMaxValue) {
        return true;
      }

      return createError({
        path,
        message: `De maximale waarde is ${formatMoney(currencyMaxValue)}`,
      });
    },
  );
});

// Multitude of thousand
addMethod(mixed, 'isMultitudeOfThousand', function multitudeThousand(error) {
  return this.test('testMultitudeOfThousand', async function testMultitudeOfThousand(value) {
    if (!value) return false;

    const { createError } = this;

    if (/0{3}$/.test(value.toString())) return true;

    return createError({
      message: error || 'Moet een veelvoud zijn van duizend',
    });
  });
});

addMethod<StringSchema>(string, 'exclude', function excludes(exclude) {
  return this.test('exclude-option', exclude, function testExcludes(value) {
    const { path, createError } = this;

    return (
      value !== exclude ||
      createError({ path, message: '${path} is een verplicht veld' })
    );
  });
});

addMethod<StringSchema>(string, 'isMinLength', function hasMinLength(min, error) {
  return this.test('testMinimumLength', min, function testMinimumLength(value = '') {
    const { createError } = this;

    if (value.length >= min) {
      return true;
    }

    return createError({
      message: error || 'Ongeldige invoer',
    });
  });
});

addMethod(string, 'containsSpecialChar', function hasSpecialChar(error) {
  return this.test('testContainsSpecialChar', function testContainsSpecialChar(value = '') {
    const { createError } = this;

    if (/[!"#$%&'()*+,./:;<=>?@[\\\]^_{|}~-]+/.test(value)) {
      return true;
    }

    return createError({
      message: error || 'Ongeldige invoer',
    });
  });
});

addMethod(string, 'containsNumber', function hasNumber(error) {
  return this.test('testContainsNumber', function testContainsNumber(value = '') {
    const { createError } = this;

    if (/(?=.*\d)/.test(value)) {
      return true;
    }

    return createError({
      message: error || 'Ongeldige invoer',
    });
  });
});

addMethod(string, 'containsCapital', function hasCapital(error) {
  return this.test('testContainsCapital', function testContainsCapital(value = '') {
    const { createError } = this;

    if (/(?=.*[A-Z])/.test(value)) {
      return true;
    }

    return createError({
      message: error || 'Ongeldige invoer',
    });
  });
});

addMethod(string, 'containsLowercase', function hasLowercase(error) {
  return this.test('testContainsLowercase', function testContainsLowercase(value = '') {
    const { createError } = this;

    if (/(?=.*[a-z])/.test(value)) {
      return true;
    }

    return createError({
      message: error || 'Ongeldige invoer',
    });
  });
});

const schema = yup.string().iban();

addMethod(string, 'isValidIban', function validIban(error) {
  return this.test('testValidIban', async function testValidIban(value) {
    const { createError } = this;
    const result = await schema.isValid(value);

    if (result) return true;

    const errorMessage = value && value.length > 0 ? 'Ongeldige IBAN' : 'IBAN is een verplicht veld';

    return createError({
      message: error || errorMessage,
    });
  });
});

addMethod(string, 'isValidPhoneNumber', function validPhoneNumber(error) {
  return this.test('testIsValidPhoneNumber', function testIsValidPhoneNumber(value = '') {
    const { path, createError } = this;

    const phoneNumber = parsePhoneNumber(value, {
      defaultCallingCode: this.parent[`${path}_countrycode`]?.replace('+', '') ?? '31',
    });

    if (phoneNumber?.isValid()) {
      return true;
    }

    const errorMessage = value && value.length > 0 ? 'Ongeldig telefoonnummer' : 'Telefoonnummer is een verplicht veld';

    return createError({
      message: error || errorMessage,
    });
  });
});

setLocale({
  mixed: {
    required: '${path} is een verplicht veld',
  },
  string: {
    email: 'Vul een geldig e-mailadres in',
    min: '${path} moet minstens ${min} karakters bevatten',
    max: '${path} moet minstens ${max} karakters bevatten',
  },
});

export { object, string, bool, setLocale, number, mixed, array };
